import React, { useState } from 'react';
import { 
  Button, 
  ButtonGroup, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import PropTypes from 'prop-types';
import { formatDateForInput } from '../utils/dateUtils';

const EnquiryActions = ({ enquiry, onOutcomeChange }) => {
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [notInterestedDialogOpen, setNotInterestedDialogOpen] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [notInterestedReason, setNotInterestedReason] = useState('');

  // Not interested reasons
  const notInterestedReasons = [
    'Only Want One-Off or Short Term Ad',
    'Too Expensive',
    "Don't Have Requested School/s",
    'School/s Full',
    'No Schools in Area',
    'Non-Responsive to Contact',
    'Chose to Advertise Elsewhere',
    'Seeking Quotes Only'
  ];

  // Handle opening follow-up date dialog
  const handleFollowUpLater = () => {
    // Set default date to 7 days from now
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    setFollowUpDate(formatDateForInput(defaultDate));
    setFollowUpNotes('');
    setFollowUpDialogOpen(true);
  };

  // Handle opening not interested dialog
  const handleNotInterested = () => {
    setNotInterestedReason('');
    setNotInterestedDialogOpen(true);
  };

  // Handle confirming follow-up date
  const handleConfirmFollowUp = () => {
    if (!followUpDate) {
      return;
    }
    
    if (onOutcomeChange) {
      onOutcomeChange(enquiry.id, 'Follow Up Later', { 
        followUpDate, 
        followUpNotes: followUpNotes.trim() || null 
      });
    }
    
    setFollowUpDialogOpen(false);
    setFollowUpDate('');
    setFollowUpNotes('');
  };

  // Handle confirming not interested reason
  const handleConfirmNotInterested = () => {
    if (!notInterestedReason) {
      return;
    }
    
    if (onOutcomeChange) {
      onOutcomeChange(enquiry.id, 'Not Interested', { whyNotInterested: notInterestedReason });
    }
    
    setNotInterestedDialogOpen(false);
    setNotInterestedReason('');
  };

  // Handle changing follow-up date for existing follow-up
  const handleChangeFollowUpDate = () => {
    setFollowUpDate(enquiry.followUpDate ? formatDateForInput(enquiry.followUpDate) : formatDateForInput(new Date()));
    setFollowUpNotes(enquiry.followUpNotes || '');
    setFollowUpDialogOpen(true);
  };

  // Get available actions based on current outcome
  const getAvailableActions = () => {
    const currentOutcome = enquiry.outcome?.toLowerCase() || 'new';
    
    switch (currentOutcome) {
      case 'new':
        return [
          { label: 'Follow Up Later', action: handleFollowUpLater, color: 'warning' },
          { label: 'Not Interested', action: handleNotInterested, color: 'error' }
        ];
      case 'follow up later':
        return [
          { label: 'Change Follow Up Date', action: handleChangeFollowUpDate, color: 'info' },
          { label: 'Not Interested', action: handleNotInterested, color: 'error' }
        ];
      default:
        return [];
    }
  };

  const availableActions = getAvailableActions();

  // If no actions are available, don't render anything
  if (availableActions.length === 0) {
    return null;
  }

  return (
    <>
      <ButtonGroup size="small" variant="text">
        {availableActions.map((actionItem, index) => (
          <Button 
            key={index}
            color={actionItem.color}
            onClick={actionItem.action}
            size="small"
            sx={{ 
              minWidth: 'auto', 
              px: 1, 
              py: 0.25, 
              fontSize: '0.75rem',
              textTransform: 'none'
            }}
          >
            {actionItem.label}
          </Button>
        ))}
      </ButtonGroup>

      {/* Follow Up Later Date Dialog */}
      <Dialog open={followUpDialogOpen} onClose={() => setFollowUpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Set Follow-Up Date</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Follow-Up Date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="When should we follow up with this enquiry?"
              required
            />
            <TextField
              fullWidth
              label="Follow-Up Notes"
              multiline
              rows={4}
              value={followUpNotes}
              onChange={(e) => setFollowUpNotes(e.target.value)}
              placeholder="Add notes about what needs to be followed up on, context, or specific actions required..."
              helperText="Optional: Add any context or notes for the follow-up"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowUpDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmFollowUp} variant="contained" color="primary" disabled={!followUpDate}>
            Set Follow-Up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Not Interested Reason Dialog */}
      <Dialog open={notInterestedDialogOpen} onClose={() => setNotInterestedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Why are they not interested?</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                value={notInterestedReason}
                onChange={(e) => setNotInterestedReason(e.target.value)}
                label="Reason"
              >
                {notInterestedReasons.map(reason => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotInterestedDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmNotInterested} variant="contained" color="primary" disabled={!notInterestedReason}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

EnquiryActions.propTypes = {
  enquiry: PropTypes.object.isRequired,
  onOutcomeChange: PropTypes.func.isRequired
};

export default EnquiryActions; 